export type TabId = "measures" | "notes" | "inspector" | "quickbase";

export interface SidePanelTab {
  id: TabId;
  label: string;
  visible: boolean;
}

const MIN_WIDTH = 260;
const MAX_WIDTH = 700;
const DEFAULT_WIDTH = 360;

/**
 * Unified right-side panel with a tab bar and drag-resizable left edge.
 * Only one tab is shown at a time. Opening a different tab switches instantly.
 * Toggling the same tab closes the panel.
 */
export class SidePanel {
  private el: HTMLElement;
  private tabBar: HTMLElement;
  private panes: Map<TabId, HTMLElement> = new Map();
  private tabBtns: Map<TabId, HTMLElement> = new Map();
  private tabs: SidePanelTab[] = [];
  private _activeTab: TabId | null = null;
  onTabChange?: (tab: TabId | null) => void;

  constructor(container: HTMLElement) {
    this.el = document.createElement("div");
    this.el.id = "side-panel";
    this.el.innerHTML = `
      <div class="sp-drag"></div>
      <div class="sp-header">
        <div class="sp-tabs"></div>
        <button class="sp-close">\u00d7</button>
      </div>
      <div class="sp-body"></div>
    `;
    container.appendChild(this.el);

    this.tabBar = this.el.querySelector(".sp-tabs")!;
    this.el.querySelector(".sp-close")!.addEventListener("click", () => this.close());

    // Drag resize from left edge
    const drag = this.el.querySelector(".sp-drag") as HTMLElement;
    let startX = 0;
    let startW = 0;

    const onMove = (e: PointerEvent) => {
      const delta = startX - e.clientX;
      const w = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + delta));
      this.el.style.width = w + "px";
    };

    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    drag.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      startX = e.clientX;
      startW = this.el.offsetWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    });

    this.el.style.width = DEFAULT_WIDTH + "px";
  }

  /** Register a tab. Returns the pane element to render content into. */
  addTab(id: TabId, label: string, visible = true): HTMLElement {
    const tab: SidePanelTab = { id, label, visible };
    this.tabs.push(tab);

    // Tab button
    const btn = document.createElement("button");
    btn.className = "sp-tab";
    btn.textContent = label;
    btn.dataset.tab = id;
    btn.style.display = visible ? "" : "none";
    btn.addEventListener("click", () => this.toggle(id));
    this.tabBar.appendChild(btn);
    this.tabBtns.set(id, btn);

    // Content pane
    const pane = document.createElement("div");
    pane.className = "sp-pane";
    pane.dataset.tab = id;
    pane.style.display = "none";
    this.el.querySelector(".sp-body")!.appendChild(pane);
    this.panes.set(id, pane);

    return pane;
  }

  /** Show or hide a tab in the tab bar. */
  setTabVisible(id: TabId, visible: boolean): void {
    const tab = this.tabs.find((t) => t.id === id);
    if (tab) tab.visible = visible;
    const btn = this.tabBtns.get(id);
    if (btn) btn.style.display = visible ? "" : "none";

    // If the hidden tab was active, close or switch
    if (!visible && this._activeTab === id) {
      const next = this.tabs.find((t) => t.visible && t.id !== id);
      if (next) this.open(next.id);
      else this.close();
    }
  }

  /** Open the panel to a specific tab. */
  open(id: TabId): void {
    this._activeTab = id;
    this.el.classList.add("open");
    this._syncPanes();
    this.onTabChange?.(id);
  }

  /** Close the entire panel. */
  close(): void {
    this._activeTab = null;
    this.el.classList.remove("open");
    this._syncPanes();
    this.onTabChange?.(null);
  }

  /** Toggle: if closed → open to tab; if open on same tab → close; if open on other → switch. */
  toggle(id: TabId): void {
    if (this._activeTab === id) this.close();
    else this.open(id);
  }

  get activeTab(): TabId | null { return this._activeTab; }
  get isOpen(): boolean { return this.el.classList.contains("open"); }

  /** Get pane element for a tab (to render content into). */
  getPane(id: TabId): HTMLElement | undefined {
    return this.panes.get(id);
  }

  private _syncPanes(): void {
    for (const [id, pane] of this.panes) {
      pane.style.display = id === this._activeTab ? "" : "none";
    }
    for (const [id, btn] of this.tabBtns) {
      btn.classList.toggle("active", id === this._activeTab);
    }
  }

  dispose(): void {
    this.el.remove();
  }
}
